import React from 'react';

interface PrivacyPolicyProps {
  appName?: string;
  onClose: () => void;
}

const PrivacyPolicyVamosly: React.FC<PrivacyPolicyProps> = ({ appName = 'Vamosly', onClose }) => {
  return (
    <div className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-xl animate-in fade-in duration-300 overflow-y-auto">
      <button 
        onClick={onClose}
        className="fixed top-8 right-8 z-[310] size-14 bg-white text-black flex items-center justify-center hover:bg-primary hover:text-white transition-all group"
      >
        <span className="material-symbols-outlined transform group-hover:rotate-90 transition-transform">close</span>
      </button>

      <div className="max-w-[900px] mx-auto px-6 py-32 text-white/80">
        <h1 className="text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-none mb-4 italic text-white flex flex-col gap-2">
          <span>Privacy</span>
          <span>Policy</span>
        </h1>
        <p className="text-xl text-primary font-bold tracking-widest uppercase mb-4">
          {appName}
        </p>
        <p className="text-sm font-bold tracking-widest uppercase mb-12 border-b border-white/10 pb-6 text-white/40">
          Effective date: 30 July 2026
        </p>

        <div className="space-y-12 font-light leading-relaxed text-lg">
          <section className="space-y-4">
            <p>
              Vamosly ("Vamosly," "the App," "we," "us," or "our") is developed and operated by <strong className="text-white">T.H.E Apps</strong>, registered at <strong className="text-white">94 9th Avenue, Parkmore</strong>. This Privacy Policy explains what information we collect when you use Vamosly, how we use it, and the choices you have.
            </p>
            <p>
              By creating an account or using Vamosly, you agree to the collection and use of information as described in this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">1. Information We Collect</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">1.1 Information you provide directly</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong className="text-white">Account information</strong>: your email address and password, used to create and secure your account.</li>
                  <li><strong className="text-white">Player names</strong>: the names of players you add to sessions, competitions, and leagues. These may include names of other people you play padel with (e.g. teammates or opponents), which you provide on their behalf when setting up a game.</li>
                  <li><strong className="text-white">Game and session data</strong>: the padel sessions, competitions, and leagues you create, including format (Americano, Mexicano, Standard Doubles), scores, round results, and standings.</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">1.2 Information collected automatically</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong className="text-white">Usage and gameplay data</strong>: statistics generated from your use of the App, such as sessions played, wins, losses, and points for/against, which are used to build player profiles and leaderboards within the App.</li>
                </ul>
                <p className="mt-4">
                  We do not request or knowingly collect your precise real-time location, payment card details, or government ID information through the App.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">2. How We Use Your Information</h2>
            <p className="mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Create and manage your account</li>
              <li>Run the core features of the App: generating pairings and rotations, tracking scores, and calculating standings for sessions, competitions, and leagues</li>
              <li>Build and display player statistics and history within your groups</li>
              <li>Enable league organisers to invite players via join codes and manage league membership</li>
              <li>Enable you to share session or league results (for example, as an image) to services outside the App at your own initiation</li>
              <li>Respond to support requests and communicate with you about the App</li>
              <li>Maintain the security and integrity of the App</li>
              <li>Improve and develop the App's features</li>
            </ul>
            <p>
              We do not sell your personal information, and we do not use your information to serve third-party advertising.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">3. How We Share Your Information</h2>
            <ul className="list-disc pl-6 space-y-4 mb-4">
              <li><strong className="text-white">With other players in your sessions, competitions, or leagues</strong>: names, scores, and statistics you or other members enter are visible to other participants of that session, competition, or league, as this is core to how the App functions.</li>
              <li><strong className="text-white">Service providers</strong>: we use third-party infrastructure providers (including our backend platform provider) to host the App and store data on our behalf. These providers process data only as needed to provide their services to us and are not permitted to use your information for their own purposes.</li>
              <li><strong className="text-white">Legal requirements</strong>: we may disclose information if required to do so by law, or in the good faith belief that such action is necessary to comply with a legal obligation, protect the rights or safety of T.H.E Apps, our users, or the public.</li>
              <li><strong className="text-white">Business transfers</strong>: if T.H.E Apps is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change and any choices you may have.</li>
            </ul>
            <p>
              We do not share your personal information with third parties for their own direct marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">4. Data Retention</h2>
            <p>
              We retain your account information and associated game data for as long as your account is active, or as needed to provide you the App's features (such as maintaining your session history and player statistics). If you delete your account, we delete or anonymise your personal information within a reasonable period, except where we are required to retain it to comply with a legal obligation, resolve disputes, or enforce our agreements.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">5. Your Rights and Choices</h2>
            <p className="mb-4">Depending on where you live, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Access the personal information we hold about you</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your account and associated personal information</li>
              <li>Object to or restrict certain processing of your information</li>
              <li>Request a copy of your information in a portable format</li>
            </ul>
            <p>
              You can delete your account directly from within the App under <strong className="text-white">Settings</strong>, which will delete your account information. You can also exercise any of the above rights by contacting us at <a href="mailto:rob@theapps.io" className="text-primary hover:underline">rob@theapps.io</a>. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">6. South African Users — POPIA</h2>
            <p>
              T.H.E Apps is based in South Africa, and where the Protection of Personal Information Act, 2013 ("POPIA") applies to our processing of your personal information, we process it in accordance with POPIA's conditions for lawful processing, including collecting information only for a specific, defined purpose, keeping it accurate and up to date, and safeguarding it against loss, damage, and unauthorised access. You may lodge a complaint with the Information Regulator of South Africa if you believe your rights under POPIA have been infringed.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">7. European and UK Users — GDPR</h2>
            <p>
              If you are located in the European Economic Area or the United Kingdom, we process your personal information on the following legal bases: performance of a contract (to provide the App's core features), our legitimate interests (such as maintaining and improving the App), and your consent where applicable. You have the right to lodge a complaint with your local data protection supervisory authority.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">8. Children's Privacy</h2>
            <p>
              Vamosly is not directed at children under the age of 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us at <a href="mailto:rob@theapps.io" className="text-primary hover:underline">rob@theapps.io</a> and we will take steps to delete it.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">9. Data Security</h2>
            <p>
              We take reasonable technical and organisational measures to protect your information against unauthorised access, alteration, disclosure, or destruction. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">10. International Data Transfers</h2>
            <p>
              Your information may be stored and processed in countries other than the one in which you live. Where we transfer personal information across borders, we take steps to ensure it receives an adequate level of protection in accordance with applicable data protection law.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. If we make material changes, we will notify you by updating the effective date at the top of this policy and, where appropriate, through the App. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">12. Contact Us</h2>
            <p className="mb-4">
              If you have any questions about this Privacy Policy or how we handle your information, contact us at:
            </p>
            <div className="bg-white/5 p-6 border border-white/10 rounded-sm">
              <strong className="text-white text-lg block mb-2">T.H.E Apps</strong>
              <p className="mb-2">94 9th Avenue, Parkmore</p>
              <a href="mailto:rob@theapps.io" className="text-primary hover:underline">Email: rob@theapps.io</a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyVamosly;
